import {
  Autocomplete,
  Box,
  Stack,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { useNavigate } from "react-router";
import { Button, CirclesSVG, Typography } from "@platform/design-system";

import { ROUTE_NAMES } from "../../constants";

import { BBEBroker } from "./types";

export const BBEPage = (): React.JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const axiosInstance = useApi();
  const [selectedBroker, setSelectedBroker] = useState<BBEBroker | null>(null);

  const brokersQuery = useQuery<BBEBroker[]>({
    queryKey: ["bbeBrokers"],
    queryFn: () =>
      axiosInstance.get<BBEBroker[]>("/bbe/v1/brokers").then((res) => res.data),
  });

  const brokers = brokersQuery.data ?? [];

  const handleNavigateToBroker = useCallback(() => {
    if (!selectedBroker) {
      return;
    }

    navigate(`/${ROUTE_NAMES.BBE}/${selectedBroker.salesforceId}`);
  }, [navigate, selectedBroker]);

  return (
    <Stack spacing={0} mt={8} flex={1}>
      <Box
        sx={{
          ...styles.sectionHeaderContainer,
          display: "flex",
          alignItems: "center",
          height: "40%",
        }}
      >
        <Box sx={styles.circlesBG} aria-hidden="true">
          <CirclesSVG />
        </Box>
        <Stack spacing={3} pl={5}>
          <Stack spacing={0} sx={{ color: "white" }}>
            <Typography
              component="span"
              variant="display-medium"
              sx={{ fontWeight: 400 }}
              color="inherit"
            >
              Welcome to Centro’s
            </Typography>
            <Box
              component="h1"
              sx={{
                m: 0,
                fontSize: 60,
                lineHeight: 1.2,
                fontWeight: 300,
                color: "inherit",
              }}
            >
              Broker Bonus Estimator
            </Box>
          </Stack>
        </Stack>
      </Box>

      <Stack spacing={3} p={5} flex={1}>
        <Typography
          variant="header-medium"
          component="span"
          sx={{ fontWeight: 400 }}
        >
          Please select a broker from the drop down list below to load the
          broker’s dashboard.
        </Typography>

        <Stack spacing={1} direction="row" width="40%">
          <Autocomplete
            data-testid="broker-autocomplete"
            sx={{ flex: 1 }}
            options={brokers}
            disabled={
              brokersQuery.isLoading ||
              brokersQuery.isError ||
              brokers.length === 0
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Broker"
                helperText={
                  brokersQuery.isError
                    ? "Unable to load brokers. Please try again."
                    : null
                }
                error={brokersQuery.isError}
              />
            )}
            getOptionLabel={(data) => data.name}
            onChange={(_e, newValue) => {
              setSelectedBroker(newValue);
            }}
            isOptionEqualToValue={(opt, val) =>
              opt.salesforceId === val.salesforceId
            }
            value={selectedBroker}
            loading={brokersQuery.isLoading}
          />

          <Button
            variant="primary"
            size="lg"
            data-testid="broker-select-button"
            onClick={handleNavigateToBroker}
            disabled={
              brokersQuery.isLoading ||
              brokersQuery.isError ||
              brokers.length === 0 ||
              !selectedBroker
            }
            sx={{ alignSelf: "center" }}
          >
            Select
          </Button>
        </Stack>

        <Typography
          variant="body-medium"
          color="textSecondary"
          sx={{ width: { xs: "100%", md: "50%" } }}
        >
          All bonus calculations are estimates based on carrier-provided data
          and assumptions. The Broker Bonus Estimator (BBE) tool is intended for
          informational purposes only and does not guarantee actual bonus
          payouts.
        </Typography>
      </Stack>
    </Stack>
  );
};

interface LandingPage<T extends Theme> {
  circlesBG: SxProps<T>;
  sectionHeaderContainer: SxProps<T>;
}

function useStyles<T extends Theme>(): LandingPage<T> {
  return {
    sectionHeaderContainer: {
      overflow: "hidden",
      // Left literal on purpose: the two teal stops (#0078A2, #4FB3CD) both map
      // to secondary-main, so converting would collapse the gradient to one
      // colour. Exempt from the sweep, like the RFP banner gradient.
      background:
        "linear-gradient(90deg, #832A80 0%, #0078A2 50.5%, #4FB3CD 100%)",
    },
    circlesBG: {
      position: "absolute",
      top: "40%",
      right: "30%",
      transform: "translate(48%, -50%) scale(1)",
      opacity: 0.5,
      zIndex: 0,
      pointerEvents: "none",
    },
  };
}
